import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconAccordion } from '@components/molecules/IconAccordion';
import { AddressOption } from '@components/molecules/AddressOption';
import { RadioButtonType } from '@components/atoms/RadioButton';
import { useData } from '@hooks/useData';
import { useGetAllNearbyPickupSpots } from '@services/useGetAllNearbyPickupSpots';
import { Input, InputTypes, InputVariants } from '@components/atoms/Input';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { useGetPickupSpotDetail } from '@services/useGetPickupSpotDetail';
import { Typography } from '@components/atoms/Typography';
import { Select, SelectType } from '@components/atoms/Select';
import { useGetAllShippingCountries } from '@services/useGetAllShippingCountries';

interface PickupSpotsFormProps {
    selectedPickupSpot: string | null;
    handleOnChangePickupSpot: (value: string | null) => void;
    selectedAddress: boolean;
    selectedAtStore: boolean;
}
interface PickupSpotsFormErrors {
    country: string[];
}

const PickupSpotsForm: FC<PickupSpotsFormProps> = ({
    selectedPickupSpot,
    handleOnChangePickupSpot,
    selectedAddress,
    selectedAtStore,
}) => {
    const { t } = useTranslation();
    const { cart, allShippingCountries, pickupSpotDetail, allNearbyPickupSpots, setAllNearbyPickupSpots } = useData();
    const {
        request: requestGetPickupSpotDetail,
        loading: loadingGetPickupSpotDetail,
        error: errorGetPickupSpotDetail,
    } = useGetPickupSpotDetail();
    const {
        request: requestGetAllNearbyPickupSpots,
        loading: loadingGetAllNearbyPickupSpots,
        error: errorGetAllNearbyPickupSpots,
        endpointInput: inputGetAllNearbyPickupSpots,
    } = useGetAllNearbyPickupSpots();
    const {
        request: requestGetAllShippingCountries,
        loading: loadingGetAllShippingCountries,
        error: errorGetAllShippingCountries,
    } = useGetAllShippingCountries();

    const translations = {
        countryLabel: t('pickups-spots-form-component.country.label'),
        countryRequiredError: t('pickups-spots-form-component.country.required-error'),
        selectCountryDefault: t('select-default-option'),
        postalCodeLabel: t('pickups-spots-form-component.postal-code.label'),
    };

    const [pickupCountry, setPickupCountry] = useState<string>('');
    const [pickupPostalCode, setPickupPostalCode] = useState<string>('');

    // errors to be displayed in the form
    const [errors, setErrors] = useState<PickupSpotsFormErrors>({
        country: [],
    });

    useEffect(() => {
        // when unmounting page, reset results
        // to prevent showing old results when returning to this accordion
        return () => {
            setAllNearbyPickupSpots(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // call services, if we don't have the information yet
    useEffect(() => {
        !allShippingCountries && !loadingGetAllShippingCountries && requestGetAllShippingCountries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allShippingCountries]);

    const validateForm: () => PickupSpotsFormErrors = () => {
        const newErrors: PickupSpotsFormErrors = {
            country: [],
        };

        pickupCountry === '' && pickupPostalCode.trim() !== '' && newErrors.country.push(translations.countryRequiredError);

        setErrors(newErrors);

        return newErrors;
    };

    const searchNearbyPickupSpots = () => {
        // must have types at least 2 characters to call service
        if (pickupPostalCode && pickupPostalCode.length >= 2) {
            const errors = validateForm();

            if (errors.country.length === 0) {
                // prevent multiple instances of service being called
                !loadingGetAllNearbyPickupSpots &&
                    requestGetAllNearbyPickupSpots({ country_code: pickupCountry, zip_code: pickupPostalCode });
            }
        } else {
            setAllNearbyPickupSpots(null);
        }
    };

    useEffect(() => {
        searchNearbyPickupSpots();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pickupPostalCode, pickupCountry]);

    // verification to make sure that the result/error matches correctly the postal code being searched
    // when GetAllNearbyPickupSpots endpoints is no longer loading
    // call service one last time
    useEffect(() => {
        if (
            !loadingGetAllNearbyPickupSpots &&
            (inputGetAllNearbyPickupSpots?.country_code !== pickupCountry ||
                inputGetAllNearbyPickupSpots?.zip_code !== pickupPostalCode)
        ) {
            searchNearbyPickupSpots();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingGetAllNearbyPickupSpots]);

    useEffect(() => {
        // if user unselected the country, reset results
        if (pickupCountry === '') {
            setAllNearbyPickupSpots(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pickupCountry]);

    // get information about pickup spot
    useEffect(() => {
        if (cart?.data?.delivery?.shipToPickupSpotId) {
            requestGetPickupSpotDetail({ id: cart?.data?.delivery?.shipToPickupSpotId });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart?.data?.delivery?.shipToPickupSpotId]);

    const handleOnChangePostalCode = (value: string) => {
        setPickupPostalCode(value);
        // reset selection
        handleOnChangePickupSpot(null);
    };

    const countriesOptionsData: { name?: string; iso_code?: string }[] = allShippingCountries?.data || [];

    const countriesAllOptions: { text: string; value: string }[] = [
        { text: translations.selectCountryDefault, value: '' },
        ...countriesOptionsData.map((country) => ({
            text: country.name || '',
            value: country.iso_code || '',
        })),
    ];

    return (
        <IconAccordion.IndentedContent>
            <div className="pb-4">
                <div className="row">
                    <div className="col-md-6 col-12">
                        <Typography color="muted" fontSize="12">
                            {translations.countryLabel}
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            options={countriesAllOptions}
                            value={pickupCountry || undefined}
                            onChange={(value) => setPickupCountry(value)}
                        />
                        {errors.country.length > 0 && <ErrorMessages messages={errors.country} />}
                    </div>
                    <div className="col-md-6 col-12 pt-2 mt-1">
                        <Input
                            type={InputTypes.text}
                            label={`${translations.postalCodeLabel}*`}
                            variant={InputVariants.labeledInput}
                            value={pickupPostalCode}
                            onChange={(value) => handleOnChangePostalCode(value)}
                            maxLength={8}
                        />
                    </div>
                    <div className="col-12 pt-2">
                        {errorGetAllShippingCountries && <ErrorMessages messages={[errorGetAllShippingCountries]} />}
                        {errorGetAllNearbyPickupSpots && <ErrorMessages messages={[errorGetAllNearbyPickupSpots]} />}
                    </div>
                </div>

                {/* display selected pickup spot */}
                {!selectedPickupSpot &&
                    !selectedAddress &&
                    !selectedAtStore &&
                    !errorGetPickupSpotDetail &&
                    !loadingGetPickupSpotDetail &&
                    cart?.data?.delivery?.shipToPickupSpotId &&
                    pickupSpotDetail?.data && (
                        <div className="pt-4">
                            <AddressOption
                                radioButton={{
                                    id: 'selected-pickup',
                                    name: 'selected-pickup',
                                    value: cart?.data?.delivery?.shipToPickupSpotId || '',
                                    type: RadioButtonType.normal,
                                    checked: true,
                                }}
                                name={pickupSpotDetail?.data?.name || ''}
                                address={
                                    pickupSpotDetail?.data?.addressLine1 +
                                    ' ' +
                                    pickupSpotDetail?.data?.addressLine2 +
                                    ' ' +
                                    pickupSpotDetail?.data?.streetNumber
                                }
                                postalCode={`${pickupSpotDetail?.data?.postalCode} ${pickupSpotDetail?.data?.city}`}
                            />
                        </div>
                    )}
                {errorGetPickupSpotDetail && <ErrorMessages messages={[errorGetPickupSpotDetail]} />}
            </div>

            {/* list of search results for pickup spots */}
            <div className="row pe-2 g-4">
                {!errorGetAllNearbyPickupSpots &&
                    !loadingGetAllNearbyPickupSpots &&
                    allNearbyPickupSpots?.data?.map((pickupSpot, index) => (
                        <div key={`pickup-${index}`} className="col-12 col-xl-6">
                            <AddressOption
                                radioButton={{
                                    id: `pickup-${index}`,
                                    name: 'pickup',
                                    value: pickupSpot.id || '',
                                    type: RadioButtonType.normal,
                                    checked: pickupSpot.id === selectedPickupSpot,
                                    onChange: handleOnChangePickupSpot,
                                }}
                                name={pickupSpot.name || ''}
                                address={pickupSpot.addressLine1 + ' ' + pickupSpot.addressLine2 + ' ' + pickupSpot.streetNumber}
                                postalCode={`${pickupSpot.postalCode} ${pickupSpot.city}`}
                            />
                        </div>
                    ))}
            </div>
        </IconAccordion.IndentedContent>
    );
};

export default PickupSpotsForm;
